import { useState, useEffect, useRef } from "react";
import { useMap } from "@vis.gl/react-google-maps";
import type { BoundingBox } from "@/types/domain";

export function useMapViewportBounds(debounceMs = 300): BoundingBox | null {
  const map = useMap();
  const [bounds, setBounds] = useState<BoundingBox | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!map) return;

    const commit = () => {
      const b = map.getBounds();
      if (!b) return;
      setBounds({
        north: b.getNorthEast().lat(),
        south: b.getSouthWest().lat(),
        east: b.getNorthEast().lng(),
        west: b.getSouthWest().lng(),
      });
    };

    // Set initial bounds immediately (no debounce)
    commit();

    const listener = map.addListener("bounds_changed", () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(commit, debounceMs);
    });

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      google.maps.event.removeListener(listener);
    };
  }, [map, debounceMs]);

  return bounds;
}
